// =====================================================================================
// INVRT Radar — web console — consent banner designer — live preview
//
// The REAL loader (web/public/r.js, served by this console at /r.js) rendering the
// CURRENT form state inside a sandboxed iframe. The page is a srcdoc document that sets
// window.__RADAR_BANNER_PREVIEW__ before the script runs; in that mode the loader draws
// the banner at once and never fetches, stores or injects a pixel. `sandbox="allow-scripts"`
// (no allow-same-origin) gives the document an opaque origin: it can run the loader and
// nothing else.
//
// Updates are debounced by 250 ms so typing does not reload the frame on every keystroke;
// the first paint is immediate.
// =====================================================================================

import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import type { BannerConfig } from "@/lib/types.c1";
import { buildPreviewSrcdoc } from "./bannerForm";

export const PREVIEW_DEBOUNCE_MS = 250;
const MOBILE_WIDTH_PX = 360;

export interface BannerPreviewProps {
  config: BannerConfig;
}

export function BannerPreview({ config }: BannerPreviewProps) {
  const [srcdoc, setSrcdoc] = useState(() => buildPreviewSrcdoc(config));
  const [width, setWidth] = useState<"desktop" | "mobile">("desktop");

  useEffect(() => {
    const handle = window.setTimeout(() => setSrcdoc(buildPreviewSrcdoc(config)), PREVIEW_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [config]);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-semibold text-ink">Preview</p>
        <div className="flex items-center gap-1" role="group" aria-label="Preview width">
          <Button
            type="button"
            size="sm"
            variant={width === "desktop" ? "secondary" : "ghost"}
            aria-pressed={width === "desktop"}
            onClick={() => setWidth("desktop")}
          >
            Desktop
          </Button>
          <Button
            type="button"
            size="sm"
            variant={width === "mobile" ? "secondary" : "ghost"}
            aria-pressed={width === "mobile"}
            onClick={() => setWidth("mobile")}
          >
            Mobile
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto rounded-card border border-line bg-surface-muted p-3">
        <iframe
          title="Banner preview"
          sandbox="allow-scripts"
          srcDoc={srcdoc}
          className="mx-auto block h-[520px] max-w-full rounded-lg border border-line bg-white"
          style={{ width: width === "mobile" ? `${MOBILE_WIDTH_PX}px` : "100%" }}
        />
      </div>
      <p className="text-xs text-ink-muted">
        Rendered by the same script your visitors load. Choices made here are not recorded. A
        logo hosted on another site may not appear in this preview; it will on your pages.
      </p>
    </div>
  );
}
